export type GeneratorType =
  | "coal"
  | "nuclear"
  | "gas-ccgt"
  | "gas-peaker"
  | "hydro"
  | "wind"
  | "solar"
  | "battery";

export type GeneratorStatus = "off" | "starting" | "on" | "stopping" | "tripped";

export interface Generator {
  id: string;
  name: string;
  type: GeneratorType;
  pMin: number;        // MW, minimum stable output when on
  pMax: number;        // MW, nameplate capacity
  cost: number;        // $/MWh marginal cost
  rampRate: number;    // MW per game-minute
  startupTime: number; // game-minutes from off → on at pMin
  output: number;      // MW, current output (negative for charging battery)
  setpoint: number;    // MW, target output (player-set or 0 when off)
  status: GeneratorStatus;
  statusTimer: number; // game-minutes remaining in starting/stopping
  // For renewables: availability fraction 0..1 (weather-driven)
  availability: number;
  // For renewables: time-varying availability (overrides static availability when set)
  availabilityCurve?: (gameMinutes: number) => number;
  // For batteries
  storageCapacity?: number; // MWh, total energy capacity
  stateOfCharge?: number;   // MWh, current energy stored
  chargeEfficiency?: number; // 0..1, default 0.92
  // Black-start capable (layer 8)
  blackStartCapable?: boolean;
}

export interface Load {
  id: string;
  name: string;
  baseDemand: number; // MW
  // Optional time-of-day curve hook (used at layer 3+)
  curve?: (gameMinutes: number) => number; // multiplier
  // Layer 8 black-start: a load can be "shed" (offline) until restored
  shed?: boolean;
}

export interface GridState {
  // Time
  gameMinutes: number;
  // Electrical
  frequency: number;   // Hz, nominal 50
  supplyMW: number;
  demandMW: number;
  // Components
  generators: Generator[];
  loads: Load[];
  // Optional network (layers 4+)
  network?: import("./network.ts").Network;
  islandedBuses?: string[];
  // Economics
  cumulativeCost: number; // $
  revenue?: number;        // for layer 9 LMP
  // Layer 9: LMP per bus, $/MWh
  lmp?: Map<string, number>;
  // Black-out flag (layer 8): true when whole grid is down
  blackedOut?: boolean;
  // Outcome
  status: "playing" | "won" | "lost";
  loseReason?: string;
  winProgressSeconds: number; // accumulated time within band
  // Layer 8 black-start: progress 0..1 of restoring loads
  restorationProgress?: number;
}

export interface SimConstants {
  nominalFrequency: number;     // 50 Hz
  tripLow: number;              // 48.5
  tripHigh: number;              // 51.5
  bandLow: number;               // 49.8 (target band)
  bandHigh: number;              // 50.2
  // Swing dynamics
  inertiaConstant: number;       // H, seconds — bigger = slower freq response
  damping: number;               // self-correction toward nominal per minute
  // Base power for the system (MW)
  systemBase: number;
}

export const DEFAULT_CONSTANTS: SimConstants = {
  nominalFrequency: 50,
  tripLow: 48.5,
  tripHigh: 51.5,
  bandLow: 49.8,
  bandHigh: 50.2,
  inertiaConstant: 6,
  damping: 0.8,
  systemBase: 1000,
};
